import type { HTMLAttributes, ReactNode } from 'react'
import styles from './AngledBand.module.css'

export type AngledBandTone = 'base' | 'elevated' | 'inverted'
export type AngledBandSlice = 'none' | 'top' | 'bottom' | 'both'

export interface AngledBandProps extends HTMLAttributes<HTMLElement> {
  /** Ground colour, resolved from theme tokens. */
  tone?: AngledBandTone
  /** Which edges are cut on the diagonal. */
  slice?: AngledBandSlice
  /** Mirror the cut, so consecutive bands do not all lean the same way. */
  reverse?: boolean
  /** Class applied to the inner content wrapper (for max-width, grid, etc). */
  contentClassName?: string
  children?: ReactNode
}

const toneClass: Record<AngledBandTone, string> = {
  base: styles.base,
  elevated: styles.elevated,
  inverted: styles.inverted,
}

const clipClass: Record<AngledBandSlice, string | undefined> = {
  none: undefined,
  top: styles.clipTop,
  bottom: styles.clipBottom,
  both: styles.clipBoth,
}

export function AngledBand({
  tone = 'base',
  slice = 'none',
  reverse = false,
  className,
  contentClassName,
  children,
  ...props
}: AngledBandProps) {
  const surfaceClasses = [
    styles.surface,
    toneClass[tone],
    clipClass[slice],
    reverse ? styles.reverse : undefined,
  ]
    .filter(Boolean)
    .join(' ')

  const contentClasses = [
    styles.content,
    slice === 'top' || slice === 'both' ? styles.padTop : undefined,
    slice === 'bottom' || slice === 'both' ? styles.padBottom : undefined,
    tone === 'inverted' ? styles.invertedContent : undefined,
    contentClassName,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <section className={[styles.band, className].filter(Boolean).join(' ')} {...props}>
      <div className={surfaceClasses} aria-hidden="true" />
      <div className={contentClasses}>{children}</div>
    </section>
  )
}
